import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
// Path from: src/features/customer/payment/screens -> src/services
// ../../../../services/stripePaymentService
import { StripePaymentService } from '../../../../services/stripePaymentService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../config/firebase/config';

const stripePromise = loadStripe('pk_test_51SJCv3Q782mNWywaarNdluqx8BkieYY4MgGWWc6f11yqaoMCoZs9sVJtWEzR89gwJTY3FtSI1LCwDsZK1DOs32uC00oEsqP7Bs');

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const CheckoutForm: React.FC<{
  mesaId: string;
  restaurantId?: string;
  subtotal: number;
  tipAmount: number;
  tipPercentage: number;
  total: number;
  restaurantName: string;
  waiterName?: string;
  staffId?: string;
}> = ({ mesaId, restaurantId, subtotal, tipAmount, tipPercentage, total, restaurantName, waiterName, staffId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const submitLock = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const getFriendlyPaymentError = (message?: string) => {
    const raw = (message || '').toLowerCase();
    if (
      raw.includes('mesa no disponible') ||
      raw.includes('cuenta ya fue pagada') ||
      raw.includes('m_not_available') ||
      raw.includes('cuenta_ya_pagada')
    ) {
      return 'No pudimos procesar el pago. Intenta nuevamente.';
    }
    return message || 'Error al procesar el pago';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;
    if (submitLock.current) return;
    submitLock.current = true;

    setProcessing(true);
    setError(null);

      try {
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) throw new Error('Card element not found');

        // Phase 1: create PaymentIntent on backend
        const intentResult = await StripePaymentService.processPurchase({
          mesaId: String(mesaId),
          subtotal: Number(subtotal),
          tipAmount: Number(tipAmount),
          tipPercentage: Number(tipPercentage),
          paymentMethod: 'stripe-card',
        });

        if (!intentResult.success || !intentResult.clientSecret) {
          throw new Error(getFriendlyPaymentError(intentResult.message || 'No se pudo crear el intento de pago'));
        }

        // Phase 2: confirm on client with Stripe.js (supports Link/3DS)
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          intentResult.clientSecret,
          {
            payment_method: {
              card: cardNumber,
              billing_details: {
                email: email || undefined,
              },
            },
          }
        );

        if (confirmError || !paymentIntent) {
          throw new Error(confirmError?.message || 'Error al confirmar el pago');
        }

        // Phase 3: finalize on backend (record + mesa update)
        const result = await StripePaymentService.processPurchase({
          mesaId: String(mesaId),
          subtotal: Number(subtotal),
          tipAmount: Number(tipAmount),
          tipPercentage: Number(tipPercentage),
          paymentMethod: 'stripe-card',
          stripePaymentIntentId: paymentIntent.id,
        });

        if (result.approved) {
          setTimeout(() => {
            navigate('/payment-success-options', {
              state: {
                transactionId: result.transactionId,
                authorizationCode: result.authorizationCode,
                amount: total,
                mesaId: String(mesaId),
                restaurantId: restaurantId ?? undefined,
                restaurantName: restaurantName,
                serverName: waiterName,
                staffId: staffId,
              },
              replace: true,
            });
          }, 800);
        } else {
          setError(getFriendlyPaymentError(result.message || 'Pago declinado'));
          setProcessing(false);
          submitLock.current = false;
        }
    } catch (err: any) {
      setError(getFriendlyPaymentError(err.message || 'Error al procesar el pago'));
      setProcessing(false);
      submitLock.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="cliente@ejemplo.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
        />
      </div>

      {/* Card Information */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Información de la tarjeta
        </label>
        
        {/* Card Number */}
        <div className="border border-gray-300 rounded-t-md px-4 py-3 bg-white">
          <CardNumberElement options={ELEMENT_OPTIONS} />
        </div>

        {/* Expiry and CVC */}
        <div className="grid grid-cols-2 gap-0">
          <div className="border border-t-0 border-r-0 border-gray-300 rounded-bl-md px-4 py-3 bg-white">
            <CardExpiryElement options={ELEMENT_OPTIONS} />
          </div>
          <div className="border border-t-0 border-gray-300 rounded-br-md px-4 py-3 bg-white">
            <CardCvcElement options={ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Pay Button */}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-blue-600 text-white py-3.5 rounded-md text-base font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm"
      >
        {processing ? 'Procesando...' : `Pagar $${total.toFixed(2)}`}
      </button>

      {/* Powered by Stripe Footer */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs text-gray-500">Procesado de forma segura por</span>
          <svg className="h-4" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
            <path fill="#635bff" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"/>
          </svg>
        </div>
      </div>
    </form>
  );
};

const StripeCheckoutScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurantName, setRestaurantName] = useState<string>('');

  const { mesaId, restaurantId, subtotal, tipPercentage, tipAmount, waiterName, staffId } = (location.state || {}) as {
    mesaId?: string;
    restaurantId?: string;
    subtotal?: number;
    tipPercentage?: number;
    tipAmount?: number;
    waiterName?: string;
    staffId?: string;
  };

  const total = (subtotal || 0) + (tipAmount || 0);

  useEffect(() => {
    const loadRestaurant = async () => {
      if (restaurantId) {
        try {
          const restaurantDoc = await getDoc(doc(db, 'restaurants', restaurantId));
          if (restaurantDoc.exists()) {
            const restaurantData = restaurantDoc.data();
            setRestaurantName(restaurantData.name || 'Restaurante');
          }
        } catch (error) {
          console.error('Error loading restaurant:', error);
        }
        return;
      }
      if (!mesaId) return;
      try {
        const mesaDoc = await getDoc(doc(db, 'mesas', mesaId));
        if (mesaDoc.exists()) {
          const mesaData = mesaDoc.data();
          const rid = mesaData?.restaurantId;
          if (rid) {
            console.warn('[LEGACY_ACCESS] StripeCheckout: restaurantId should be passed in state; read from top-level mesas.');
            const restaurantDoc = await getDoc(doc(db, 'restaurants', rid));
            if (restaurantDoc.exists()) {
              setRestaurantName(restaurantDoc.data()?.name || 'Restaurante');
            }
          }
        }
      } catch (error) {
        console.error('Error loading restaurant:', error);
      }
    };

    loadRestaurant();
  }, [mesaId, restaurantId]);

  return (
    <div className="min-h-screen bg-white animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Header with Amount */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 pr-10">
          RD$ {total.toFixed(2)}
        </h1>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 sm:px-6 py-6">
        <Elements stripe={stripePromise}>
          <CheckoutForm
            mesaId={mesaId || ''}
            restaurantId={restaurantId}
            subtotal={subtotal || 0}
            tipAmount={tipAmount || 0}
            tipPercentage={tipPercentage || 0}
            total={total}
            restaurantName={restaurantName}
            waiterName={waiterName}
            staffId={staffId}
          />
        </Elements>
      </div>
    </div>
  );
};

export default StripeCheckoutScreen;